import { useSyncExternalStore } from "react";
import { UserInfo } from "../types/socket-events";

type RoomKey = string;
interface PresenceState {
    rooms: Record<RoomKey, readonly UserInfo[]>;
}

const EMPTY: readonly UserInfo[] = [];
const state: PresenceState = { rooms: {} };
const listeners = new Set<() => void>();

const subscribe = (l: () => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
};
const getSnapshot = () => state;

function canonical(users: readonly UserInfo[]) {
    return Object.values([...users]
        .reduce<Record<number, UserInfo>>((acc, u) => {
            acc[u.id_user] = u;
            return acc;
        }, {}));
}

function sameUsers(a: readonly UserInfo[], b: readonly UserInfo[]) {
    if (a.length !== b.length) return false;
    return a.every((u, i) => u.id_user === b[i].id_user);
}

export function updatePresence(room: RoomKey, incoming: readonly UserInfo[]) {
    const prev = state.rooms[room] ?? EMPTY;

    // 1. Deduplica o payload que chegou
    const next = canonical(incoming).sort((a, b) => a.id_user - b.id_user);

    // 2. Se não mudou, nada a fazer
    if (sameUsers(prev, next)) return;

    // 3. Atualiza (ou limpa) o estado
    if (next.length === 0) {
        // remove a key para evitar fantasmas
        const { [room]: _, ...rest } = state.rooms;
        state.rooms = rest;
    } else {
        state.rooms = { ...state.rooms, [room]: next };
    }

    // 4. Notifica assinantes
    listeners.forEach((l) => l());
}

export function usePresenceStore<T>(
    selector: (s: PresenceState) => T
): T {
    return useSyncExternalStore(
        subscribe,
        () => selector(getSnapshot()),
        () => selector(getSnapshot())
    );
}